import type { CSSProperties } from 'react';
import type { DraggableStyle } from '@hello-pangea/dnd';

import type { PlatformDragBoundaryRect } from './boundary';

export interface DragAndDropDesignTokens {
    borderRadiusLG: number;
    boxShadowSecondary: string;
    colorBgBase: string;
    colorBgElevated: string;
    colorFillSecondary: string;
    colorTextQuaternary: string;
    motionDurationFast: string;
    motionEaseInOut: string;
}

/** Semantic indicator color shared by box, line, and grid DnD adapters. */
export const getDropIndicatorColor = (token: DragAndDropDesignTokens): string => token.colorTextQuaternary;

export const getDragActiveStyle = (
    style: CSSProperties | undefined,
    token: DragAndDropDesignTokens,
): CSSProperties => ({
    ...style,
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    isolation: 'isolate',
    opacity: 0.98,
    overflow: 'hidden',
    transformOrigin: 'center',
    userSelect: 'none',
    willChange: 'transform',
});

export const getDragPreviewStyle = (
    style: DraggableStyle | undefined,
    isDragging: boolean,
    token: DragAndDropDesignTokens,
    boundaryRect?: PlatformDragBoundaryRect | null,
): DraggableStyle | undefined => {
    if (!style || !isDragging) {
        return style;
    }

    return getDragActiveStyle(getConstrainedDragStyle(style, boundaryRect), token) as DraggableStyle;
};

const TRANSLATE_PATTERN = /^translate\((-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\)$/;

/** Keeps the engine-owned preview box entirely inside its resolved platform surface. */
export const getConstrainedDragStyle = (
    style: DraggableStyle | undefined,
    boundaryRect?: PlatformDragBoundaryRect | null,
): DraggableStyle | undefined => {
    if (!style || !boundaryRect || !('position' in style) || style.position !== 'fixed') {
        return style;
    }

    const transformMatch = style.transform?.match(TRANSLATE_PATTERN);
    const translatedX = transformMatch ? Number(transformMatch[1]) : 0;
    const translatedY = transformMatch ? Number(transformMatch[2]) : 0;
    const renderedLeft = style.left + translatedX;
    const renderedTop = style.top + translatedY;
    const maximumLeft = Math.max(boundaryRect.left, boundaryRect.right - style.width);
    const maximumTop = Math.max(boundaryRect.top, boundaryRect.bottom - style.height);
    const constrainedLeft = Math.min(Math.max(renderedLeft, boundaryRect.left), maximumLeft);
    const constrainedTop = Math.min(Math.max(renderedTop, boundaryRect.top), maximumTop);
    const constrainedX = constrainedLeft - style.left;
    const constrainedY = constrainedTop - style.top;

    return {
        ...style,
        transform:
            constrainedX === 0 && constrainedY === 0 ? undefined : `translate(${constrainedX}px, ${constrainedY}px)`,
    };
};

export const getDropZoneIdleStyle = (
    style: CSSProperties | undefined,
    token: DragAndDropDesignTokens,
): CSSProperties => ({
    borderRadius: token.borderRadiusLG,
    boxSizing: 'border-box',
    outlineColor: 'transparent',
    outlineOffset: '-1px',
    outlineStyle: 'dashed',
    outlineWidth: 1,
    transition: `background-color ${token.motionDurationFast} ${token.motionEaseInOut}, outline-color ${token.motionDurationFast} ${token.motionEaseInOut}`,
    ...style,
});

export const getDropZoneStyle = (style: CSSProperties | undefined, token: DragAndDropDesignTokens): CSSProperties => ({
    ...getDropZoneIdleStyle(style, token),
    backgroundColor: token.colorFillSecondary,
    outlineColor: getDropIndicatorColor(token),
});

/**
 * Uses the platform drop-zone treatment for a visual insertion marker without
 * allowing that marker to affect the drag engine's measured layout.
 */
export const getDropPlaceholderStyle = (
    style: CSSProperties | undefined,
    token: DragAndDropDesignTokens,
): CSSProperties => ({
    ...getDropZoneStyle(style, token),
    pointerEvents: 'none',
});
